/**
 * /studio/app/:id — Éditeur mini-constructeur (Phase 9).
 *
 * Onglets : Constructeur (blocs + settings) | Aperçu | Soumissions
 * Sauvegarde manuelle (bouton), publier/dépublier, retour /studio.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { useAuth } from '@/lib/auth';
import { useT, useLang } from '@/lib/i18n';
import { setPageMeta } from '@/lib/seo';
import {
  getApp, updateApp, publishApp, unpublishApp, listSubmissions, downloadSubmissionsCsv,
} from '@/lib/apps-api';
import { VERTICALS, VERTICALS_BY_CODE } from '@/lib/verticals';
import {
  makeBlock, moveBlockUp, moveBlockDown, replaceBlock, removeBlock, slugify, validateSlug,
} from '@/lib/builder';
import { BlockEditor, BlockPublicRenderer } from '@/components/builder/BlockCards';
import { track } from '@/lib/analytics';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Globe, EyeOff, Loader2, MessageSquare, ClipboardList, FileText,
  Plus, ExternalLink, Sparkles, Download, RefreshCw,
} from 'lucide-react';

function TabBtn({ active, onClick, icon: Icon, label, testid, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
        active ? 'bg-[var(--om-ink)] text-[var(--om-btn-primary-fg)]' : 'text-[var(--om-muted)] hover:text-[var(--om-ink)]'
      }`}
      data-testid={testid}
    >
      <Icon size={12} strokeWidth={2.4} /> {label}
      {typeof badge === 'number' && badge > 0 && (
        <span className="ml-1 rounded-full bg-[var(--om-ink)]/10 px-1.5 py-0.5 text-[10px] tabular-nums text-[var(--om-ink)]">{badge}</span>
      )}
    </button>
  );
}

export default function StudioAppEditor() {
  const t = useT();
  const { lang } = useLang();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { id: appId } = useParams();
  const [state, setState] = useState({ loading: true, app: null, concept: null, error: null });
  const [tab, setTab] = useState('build');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [subs, setSubs] = useState({ loading: false, items: [], total: 0 });
  const [slugError, setSlugError] = useState(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    setPageMeta({
      title: `${t('builder.title')} — OpenMeta`,
      description: t('builder.description'),
      path: `/studio/app/${appId}`,
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang, appId]);

  useEffect(() => {
    if (!authLoading && user === false) navigate(`/connexion?next=/studio/app/${appId}`, { replace: true });
  }, [authLoading, user, navigate, appId]);

  const reload = async () => {
    setState((s) => ({ ...s, loading: true }));
    const res = await getApp(appId);
    if (res.ok) {
      setState({ loading: false, app: res.app, concept: res.concept_summary, error: null });
      dirtyRef.current = false;
    } else {
      setState({ loading: false, app: null, concept: null, error: res.status === 404 ? 'not_found' : 'error' });
    }
  };
  useEffect(() => { if (user) reload(); }, [user, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSubs = async () => {
    setSubs((s) => ({ ...s, loading: true }));
    const res = await listSubmissions(appId, { page: 1, limit: 100 });
    setSubs({ loading: false, items: res.ok ? (res.items || []) : [], total: res.ok ? (res.total || 0) : 0 });
  };
  useEffect(() => { if (tab === 'subs' && subs.items.length === 0) loadSubs(); /* eslint-disable-next-line */ }, [tab]);

  const app = state.app;
  const accent = useMemo(() => VERTICALS_BY_CODE[app?.vertical]?.color || '#0A0E1A', [app?.vertical]);

  if (state.loading || !app) {
    return (
      <div data-testid="page-studio-editor">
        <Navbar />
        <main className="min-h-[60vh] pt-[128px] md:pt-[168px]">
          <div className="om-container text-sm text-[var(--om-muted-2)]">
            {state.error === 'not_found' ? t('builder.errors.not_found') : '…'}
          </div>
        </main>
      </div>
    );
  }

  // ---------- Mutations locales ----------
  const patch = (partial) => {
    dirtyRef.current = true;
    setState((s) => ({ ...s, app: { ...s.app, ...partial } }));
  };
  const patchBlock = (i, partial) => {
    const next = replaceBlock(app.blocks, i, partial);
    patch({ blocks: next });
  };
  const addBlock = (type) => {
    if ((app.blocks || []).length >= 8) {
      toast.error(t('builder.errors.max_blocks'));
      return;
    }
    patch({ blocks: [...(app.blocks || []), makeBlock(type, app.lang)] });
  };

  // ---------- Slug UI ----------
  const handleSlugChange = (raw) => {
    const s = (raw || '').toLowerCase().replace(/\s+/g, '-');
    setSlugError(validateSlug(s));
    patch({ slug: s });
  };

  // ---------- Save ----------
  const save = async () => {
    if (saving) return;
    if (slugError) {
      toast.error(t(slugError));
      return;
    }
    setSaving(true);
    const payload = {
      name: app.name,
      slug: app.slug,
      vertical: app.vertical,
      lang: app.lang,
      description: app.description,
      blocks: app.blocks,
    };
    const res = await updateApp(appId, payload);
    setSaving(false);
    if (res.ok) {
      dirtyRef.current = false;
      setState((s) => ({ ...s, app: res.app, concept: res.concept_summary }));
      toast.success(t('builder.saved'));
    } else if (res.status === 409) {
      setSlugError('builder.errors.slug_taken');
      toast.error(t('builder.errors.slug_taken'));
    } else if (res.status === 400) {
      toast.error(res.detail || t('builder.errors.invalid'));
    } else {
      toast.error(t('builder.errors.save_failed'));
    }
  };

  const doPublish = async () => {
    if (publishing) return;
    if (dirtyRef.current) {
      toast(t('builder.save_before_publish'));
      return;
    }
    setPublishing(true);
    const action = app.status === 'published' ? unpublishApp : publishApp;
    const res = await action(appId);
    setPublishing(false);
    if (res.ok) {
      if (app.status !== 'published') track('app_published', { lang });
      toast.success(app.status === 'published' ? t('builder.unpublished') : t('builder.published'));
      reload();
    } else if (res.status === 402) {
      toast.error(res.detail || t('builder.errors.quota'));
    } else {
      toast.error(res.detail || t('builder.errors.publish_failed'));
    }
  };

  const blocks = app.blocks || [];
  const isPub = app.status === 'published';

  return (
    <div data-testid="page-studio-editor">
      <Navbar />
      <main className="pt-[112px] md:pt-[132px] pb-24">
        <div className="om-container">
          {/* Header */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Link to="/studio" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm" data-testid="editor-back-btn">
              <ArrowLeft size={12} strokeWidth={2.4} /> {t('builder.back_studio')}
            </Link>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                isPub ? 'bg-emerald-500/15 text-emerald-700' : 'bg-[var(--om-ink)]/5 text-[var(--om-muted)]'
              }`}
              data-testid="editor-status-badge"
            >
              {isPub ? <Globe size={9} strokeWidth={2.6} /> : <EyeOff size={9} strokeWidth={2.6} />}
              {isPub ? t('studio.status_published') : t('studio.status_draft')}
            </span>
            {isPub && (
              <a href={`/app/${app.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--om-muted)] hover:text-[var(--om-ink)]" data-testid="editor-open-public">
                /app/{app.slug} <ExternalLink size={10} strokeWidth={2.4} />
              </a>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-4 py-2 text-xs font-bold text-[var(--om-ink)] hover:shadow-sm disabled:opacity-60"
                data-testid="editor-save-btn"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} strokeWidth={2.4} />}
                {saving ? t('builder.saving') : t('builder.save')}
              </button>
              <button
                type="button"
                onClick={doPublish}
                disabled={publishing}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white disabled:opacity-60"
                style={{ background: isPub ? '#8892A6' : accent }}
                data-testid="editor-publish-btn"
              >
                {publishing ? <Loader2 size={12} className="animate-spin" /> : (isPub ? <EyeOff size={12} strokeWidth={2.4} /> : <Globe size={12} strokeWidth={2.4} />)}
                {isPub ? t('studio.unpublish') : t('studio.publish')}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6 flex flex-wrap items-center gap-1 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-1">
            <TabBtn active={tab === 'build'} onClick={() => setTab('build')} icon={Sparkles} label={t('builder.tabs.build')} testid="editor-tab-build" />
            <TabBtn active={tab === 'preview'} onClick={() => setTab('preview')} icon={Globe} label={t('builder.tabs.preview')} testid="editor-tab-preview" />
            <TabBtn active={tab === 'subs'} onClick={() => setTab('subs')} icon={ClipboardList} label={t('builder.tabs.submissions')} testid="editor-tab-submissions" badge={app.submissions_count} />
          </div>

          {tab === 'build' && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
              <div className="space-y-4">
                {/* Settings */}
                <div className="rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.settings')}</div>
                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="block">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.name_label')}</div>
                      <input
                        type="text"
                        className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-2 text-sm text-[var(--om-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--om-ink)]/10"
                        value={app.name || ''}
                        onChange={(e) => patch({ name: e.target.value })}
                        onBlur={() => { if (!app.slug || app.slug === '') { const s = slugify(app.name); handleSlugChange(s); } }}
                        maxLength={80}
                        data-testid="editor-name-input"
                      />
                    </label>
                    <label className="block">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.slug_label')}</div>
                      <div className="flex items-center rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3">
                        <span className="text-[11px] text-[var(--om-muted-2)]">/app/</span>
                        <input
                          type="text"
                          className="flex-1 bg-transparent py-2 text-sm text-[var(--om-ink)] focus:outline-none"
                          value={app.slug || ''}
                          onChange={(e) => handleSlugChange(e.target.value)}
                          maxLength={40}
                          data-testid="editor-slug-input"
                        />
                      </div>
                      {slugError && <div className="mt-1 text-[10px] text-rose-500" data-testid="editor-slug-error">{t(slugError)}</div>}
                    </label>
                    <label className="block">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.vertical_label')}</div>
                      <select
                        className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-2 text-sm text-[var(--om-ink)] focus:outline-none"
                        value={app.vertical}
                        onChange={(e) => patch({ vertical: e.target.value })}
                        data-testid="editor-vertical-select"
                      >
                        {VERTICALS.map((v) => <option key={v.code} value={v.code}>{v.code}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.lang_label')}</div>
                      <select
                        className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-2 text-sm text-[var(--om-ink)] focus:outline-none"
                        value={app.lang}
                        onChange={(e) => patch({ lang: e.target.value })}
                        data-testid="editor-lang-select"
                      >
                        <option value="fr">FR</option>
                        <option value="en">EN</option>
                      </select>
                    </label>
                    <label className="block md:col-span-2">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.description_label')}</div>
                      <textarea
                        className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-2 text-sm text-[var(--om-ink)] focus:outline-none min-h-[60px]"
                        value={app.description || ''}
                        onChange={(e) => patch({ description: e.target.value })}
                        maxLength={500}
                        data-testid="editor-description-input"
                      />
                    </label>
                  </div>
                </div>

                {/* Blocks */}
                <div className="space-y-3">
                  {blocks.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-[var(--om-border)] bg-[var(--om-bg-pure)] p-8 text-center text-sm text-[var(--om-muted)]" data-testid="editor-blocks-empty">
                      {t('builder.blocks_empty')}
                    </div>
                  )}
                  {blocks.map((b, i) => (
                    <BlockEditor
                      key={b.id}
                      block={b}
                      accent={accent}
                      index={i}
                      onChange={(partialCfg) => patchBlock(i, partialCfg)}
                      onRemove={() => patch({ blocks: removeBlock(blocks, i) })}
                      onMoveUp={() => patch({ blocks: moveBlockUp(blocks, i) })}
                      onMoveDown={() => patch({ blocks: moveBlockDown(blocks, i) })}
                      disableUp={i === 0}
                      disableDown={i === blocks.length - 1}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.add_block')}</span>
                  <button type="button" onClick={() => addBlock('content')} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm" data-testid="editor-add-content">
                    <FileText size={11} strokeWidth={2.4} /> {t('builder.blocks.content')}
                  </button>
                  <button type="button" onClick={() => addBlock('chatbot')} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm" data-testid="editor-add-chatbot">
                    <MessageSquare size={11} strokeWidth={2.4} /> {t('builder.blocks.chatbot')}
                  </button>
                  <button type="button" onClick={() => addBlock('form')} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm" data-testid="editor-add-form">
                    <ClipboardList size={11} strokeWidth={2.4} /> {t('builder.blocks.form')}
                  </button>
                </div>
              </div>

              {/* Sidebar : plan du concept */}
              <aside className="space-y-4">
                {state.concept && (
                  <div className="rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-5" data-testid="editor-concept-panel">
                    <div className="text-xs font-bold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.concept_panel_title')}</div>
                    <h3 className="mt-2 text-base font-bold text-[var(--om-ink)]">{state.concept.app_name}</h3>
                    <p className="mt-1 text-xs text-[var(--om-muted)]">{state.concept.pitch}</p>
                    <div className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.concept_modules')}</div>
                    <ul className="mt-2 space-y-1.5">
                      {(state.concept.modules || []).map((m, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-[var(--om-ink-soft)]">
                          <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: accent }} />
                          <span><span className="font-semibold">{m.name}</span>{m.ai_powered && <span className="ml-1 rounded-full bg-emerald-100 px-1 py-0.5 text-[8px] font-bold uppercase text-emerald-700">IA</span>}<br /><span className="text-[var(--om-muted)]">{m.description}</span></span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.concept_screens')}</div>
                    <ul className="mt-2 space-y-1.5">
                      {(state.concept.key_screens || []).map((s, i) => (
                        <li key={i} className="text-xs text-[var(--om-ink-soft)]">
                          <span className="font-semibold">{s.name}</span> — <span className="text-[var(--om-muted)]">{s.description}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.tips_title')}</div>
                  <ul className="mt-3 space-y-2 text-xs text-[var(--om-ink-soft)]">
                    <li>{t('builder.tips.slug')}</li>
                    <li>{t('builder.tips.persona')}</li>
                    <li>{t('builder.tips.publish')}</li>
                  </ul>
                </div>
              </aside>
            </div>
          )}

          {tab === 'preview' && (
            <PreviewCanvas app={app} accent={accent} />
          )}

          {tab === 'subs' && (
            <SubmissionsPanel appId={appId} slug={app.slug} subs={subs} loadSubs={loadSubs} />
          )}
        </div>
      </main>
    </div>
  );
}

function PreviewCanvas({ app, accent }) {
  const t = useT();
  return (
    <div className="rounded-3xl border border-[var(--om-border)] bg-[var(--om-bg)] p-4 md:p-8" data-testid="editor-preview">
      <div className="mb-4 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">{t('builder.preview_note')}</div>
      <div className="rounded-3xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-6 md:p-10">
        <header className="mb-6 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-full text-white" style={{ background: accent }}>
            <Sparkles size={16} strokeWidth={2.4} />
          </span>
          <div>
            <h1 className="text-2xl font-extrabold text-[var(--om-ink)]">{app.name}</h1>
            <div className="text-xs text-[var(--om-muted-2)]">/app/{app.slug}</div>
          </div>
        </header>
        <div className="space-y-4">
          {(app.blocks || []).map((b) => (
            <BlockPublicRenderer key={b.id} block={b} slug={app.slug} accent={accent} lang={app.lang} preview />
          ))}
          {(!app.blocks || app.blocks.length === 0) && (
            <div className="rounded-2xl border border-dashed border-[var(--om-border)] p-8 text-center text-sm text-[var(--om-muted)]">
              {t('builder.blocks_empty')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SubmissionsPanel({ appId, slug, subs, loadSubs }) {
  const t = useT();
  const { lang } = useLang();
  const cols = useMemo(() => {
    const set = new Set();
    subs.items.forEach((s) => {
      Object.keys(s.data || {}).forEach((k) => set.add(k));
    });
    return Array.from(set);
  }, [subs.items]);

  const download = async () => {
    try {
      await downloadSubmissionsCsv(appId, slug);
      toast.success(t('builder.export_ok'));
    } catch {
      toast.error(t('builder.export_failed'));
    }
  };

  return (
    <div data-testid="editor-submissions">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
          {subs.total} {t('builder.total_submissions')}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={loadSubs} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm" data-testid="submissions-refresh">
            <RefreshCw size={11} strokeWidth={2.4} /> {t('builder.refresh')}
          </button>
          <button type="button" onClick={download} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--om-ink)] px-3 py-1.5 text-xs font-bold text-white" data-testid="submissions-export">
            <Download size={11} strokeWidth={2.4} /> {t('builder.export_csv')}
          </button>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)]">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="submissions-table">
            <thead className="bg-[var(--om-bg)]">
              <tr>
                <th className="whitespace-nowrap px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Date</th>
                {cols.map((c) => (
                  <th key={c} className="whitespace-nowrap px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--om-border)]">
              {subs.loading && (
                <tr><td colSpan={cols.length + 1} className="px-4 py-8 text-center text-sm text-[var(--om-muted)]">…</td></tr>
              )}
              {!subs.loading && subs.items.length === 0 && (
                <tr><td colSpan={cols.length + 1} className="px-4 py-8 text-center text-sm text-[var(--om-muted-2)]" data-testid="submissions-empty">{t('builder.no_submissions_yet')}</td></tr>
              )}
              {subs.items.map((s, i) => (
                <tr key={s.id || i}>
                  <td className="whitespace-nowrap px-4 py-3 text-xs tabular-nums text-[var(--om-muted)]">
                    {new Date(s.created_at).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  {cols.map((c) => (
                    <td key={c} className="max-w-[240px] px-4 py-3 text-xs text-[var(--om-ink)]" title={(s.data || {})[c] || ''}>
                      <div className="truncate">{(s.data || {})[c] || '—'}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
